import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { Button } from '../../components/ui/button'
import { Label } from '../../components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import {
  Settings as SettingsIcon,
  Plug,
  Smartphone,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react'
import { CredentialSetup } from '../../components/mobile/CredentialSetup'
import { getCredentialsStatus } from '../../lib/mobile-api'
import { Badge } from '../../components/ui/badge'
import { IntegrationIcon } from '../../components/integrations/IntegrationIcon'

interface Organization {
  id: number
  name: string
  description: string | null
  show_archived_projects: boolean
  created_at: string
  updated_at: string
}

interface EnvironmentVariable {
  id: number
  key: string
  value: string
  description: string | null
}

interface Integration {
  id: number
  integration_type: string
  status: string
  last_error: string | null
  template: {
    name: string
    description: string
    icon: string
    documentation_url: string
    environment_variables: Array<{
      key: string
      label: string
      description: string
      required: boolean
      placeholder: string
      sensitive: boolean
    }>
  } | null
  environment_variables: EnvironmentVariable[]
}

interface OrganizationSettingsProps {
  organization: Organization & {
    apple_credentials_configured: boolean
  }
  integrations: Integration[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

function Settings({ organization, integrations, auth }: OrganizationSettingsProps) {
  const isSuperAdmin = auth?.user?.super_admin === true
  const { t } = useTranslation('organizations')
  const [showArchived, setShowArchived] = useState(organization.show_archived_projects)
  const [isSaving, setIsSaving] = useState(false)
  const [expandedIntegration, setExpandedIntegration] = useState<number | null>(null)
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [appleConfigured, setAppleConfigured] = useState(organization.apple_credentials_configured)

  const refreshCredentials = useCallback(async () => {
    try {
      const status = await getCredentialsStatus()
      setAppleConfigured(status.apple_configured)
    } catch {
      // Ignore
    }
  }, [])

  const handleSaveSettings = () => {
    setIsSaving(true)
    router.patch('/organization/update_settings', {
      organization: {
        show_archived_projects: showArchived
      }
    }, {
      onFinish: () => setIsSaving(false)
    })
  }

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <Badge variant="default" className="text-xs bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('integrations.statusConnected')}
          </Badge>
        )
      case 'error':
        return (
          <Badge variant="destructive" className="text-xs">
            <XCircle className="h-3 w-3 mr-1" />
            {t('integrations.statusError')}
          </Badge>
        )
      default:
        return <Badge variant="secondary" className="text-xs">{t('integrations.statusDisconnected')}</Badge>
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="mb-8 max-md:mx-auto max-md:flex max-md:w-fit">
                <TabsTrigger value="general" className="gap-2">
                  <SettingsIcon className="h-4 w-4" />
                  {t('settings.general')}
                </TabsTrigger>
                <TabsTrigger value="integrations" className="gap-2">
                  <Plug className="h-4 w-4" />
                  {t('settings.integrations')}
                </TabsTrigger>
                {isSuperAdmin && (
                  <TabsTrigger value="mobile" className="gap-2">
                    <Smartphone className="h-4 w-4" />
                    {t('settings.mobilePublishing', 'Mobile Publishing')}
                  </TabsTrigger>
                )}
              </TabsList>

              {/* General Settings Tab */}
              <TabsContent value="general">
                <div className="space-y-8">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('display.title')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{t('display.subtitle')}</p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="show_archived_projects"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                    <div className="flex-1">
                      <Label htmlFor="show_archived_projects" className="text-sm font-medium text-foreground cursor-pointer">
                        {t('display.showArchived')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('display.showArchivedDescription')}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveSettings} disabled={isSaving} className="max-sm:w-full">
                      {isSaving ? t('actions.saving') : t('actions.save')}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Integrations Tab */}
              <TabsContent value="integrations">
                <div className="space-y-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('integrations.title')}
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">{t('integrations.subtitle')}</p>
                    </div>
                    {integrations.length > 0 && (
                      <Badge variant="secondary" className="text-sm px-3 py-1">
                        {t('integrations.count', { count: integrations.length })}
                      </Badge>
                    )}
                  </div>

                  {integrations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed rounded-lg">
                      <div className="rounded-full bg-primary/10 p-4 mb-4">
                        <Plug className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="font-semibold text-xl mb-2 text-foreground">{t('integrations.noIntegrations')}</h3>
                      <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                        {t('integrations.noIntegrationsDescription')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {integrations.map((integration) => (
                        <div
                          key={integration.id}
                          className="border rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedIntegration(
                              expandedIntegration === integration.id ? null : integration.id
                            )}
                            className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <IntegrationIcon integrationType={integration.integration_type} />
                                <div>
                                  <h3 className="font-semibold text-sm text-foreground">
                                    {integration.template?.name || integration.integration_type}
                                  </h3>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {integration.template?.description || t('integrations.thirdPartyDescription')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {getStatusBadge(integration.status)}
                                {expandedIntegration === integration.id ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </button>

                          {expandedIntegration === integration.id && (
                            <div className="border-t px-4 py-4 bg-muted/50">
                              <div className="space-y-4">
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-foreground">{t('integrations.environmentVariables')}</h4>
                                    <span className="text-xs text-muted-foreground">
                                      {t('integrations.variableCount', { count: integration.environment_variables.length })}
                                    </span>
                                  </div>
                                  {integration.environment_variables.length === 0 ? (
                                    <div className="text-center py-6 rounded-md border border-dashed">
                                      <p className="text-sm text-muted-foreground">
                                        {t('integrations.noVariables')}
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {integration.environment_variables.map((envVar) => {
                                        const templateVar = integration.template?.environment_variables.find(
                                          v => v.key === envVar.key
                                        )
                                        return (
                                          <div
                                            key={envVar.id}
                                            className="border rounded-md p-3"
                                          >
                                            <div className="flex items-start justify-between gap-3">
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <code className="text-xs font-semibold text-foreground bg-muted px-2 py-0.5 rounded">
                                                    {envVar.key}
                                                  </code>
                                                  {templateVar?.required && (
                                                    <span className="text-xs text-destructive font-medium">*</span>
                                                  )}
                                                </div>
                                                {templateVar?.label && (
                                                  <div className="text-xs text-foreground/80 mb-1">
                                                    {templateVar.label}
                                                  </div>
                                                )}
                                                {(envVar.description || templateVar?.description) && (
                                                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                                    {envVar.description || templateVar?.description}
                                                  </p>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded break-all max-w-[300px]">
                                                  {visibleValues.has(envVar.id) ? envVar.value : '••••••••'}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={() => toggleValueVisibility(envVar.id)}
                                                  className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                                >
                                                  {visibleValues.has(envVar.id) ? (
                                                    <EyeOff className="h-4 w-4" />
                                                  ) : (
                                                    <Eye className="h-4 w-4" />
                                                  )}
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>

                                {integration.template?.documentation_url && (
                                  <div className="pt-2">
                                    <a
                                      href={integration.template.documentation_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                    >
                                      {t('integrations.viewDocs')}
                                      <ChevronRight className="h-3 w-3" />
                                    </a>
                                  </div>
                                )}

                                {integration.last_error && (
                                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                    <div className="flex items-start gap-2">
                                      <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1">
                                        <h5 className="text-xs font-semibold text-red-900 mb-1">{t('integrations.error')}</h5>
                                        <p className="text-xs text-red-800">{integration.last_error}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Mobile Publishing Tab (super admin only) */}
              {isSuperAdmin && (
                <TabsContent value="mobile">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('settings.mobilePublishing', 'Mobile Publishing')}
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('settings.mobilePublishingDescription', 'Configure Apple and Google developer credentials for deploying mobile apps to TestFlight and Play Store.')}
                      </p>
                    </div>
                    <CredentialSetup
                      appleConfigured={appleConfigured}
                      onUpdate={refreshCredentials}
                    />
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  )
}

Settings.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Settings
